import React, { useCallback, useEffect, useState } from 'react';
import apiClient from '../utils/api';
import './CompanyInventoryPage.css';

/*
 * A brokerage's own stock.
 *
 * Only their units are listed — the server filters on owner_company_id in the
 * query, so a competitor's unit in the same compound never reaches the browser.
 * Organised by compound because that is how a broker thinks about their book,
 * and because one sheet routinely spans several — one file has produced nine.
 *
 * Two tabs, because there are two questions. "What do I cover?" is answered by
 * places, with photographs, which is how a broker recognises a compound long
 * before reading its name. "What am I selling?" is answered by a table of
 * stock. Stacking every compound's full table down one page answered neither:
 * reaching the second compound meant scrolling past a hundred rows.
 *
 * Editing is inline and per-field. A form that made you open a modal to change
 * one price would not get used; the whole point is correcting a number someone
 * spotted while reading the row.
 */
export default function CompanyInventoryPage() {
  const [units, setUnits] = useState([]);
  const [compoundInfo, setCompoundInfo] = useState([]);
  const [openSummary, setOpenSummary] = useState(null);   // compound name
  const [tab, setTab] = useState('compounds');
  const [selected, setSelected] = useState(null);   // compound name
  const [query, setQuery] = useState('');
  const [compoundQuery, setCompoundQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);   // unit id
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const u = await apiClient.get('/inventory/units', { params: { limit: 500 } });
      setUnits(u.data?.units || []);
    } catch (e) {
      setError(readError(e, 'Could not load your inventory.'));
    } finally {
      setLoading(false);
    }

    /* Deliberately not awaited alongside the units. The summaries are context;
       the units are the page. Loading them together meant a failure in the
       secondary call blanked the primary content — which is exactly what a
       broker cannot afford mid-edit, and what happens for real in the window
       between a frontend deploy and the backend catching up. */
    try {
      const c = await apiClient.get('/inventory/compounds');
      setCompoundInfo(c.data?.compounds || []);
    } catch {
      setCompoundInfo([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function startEdit(u) {
    setEditing(u.id);
    setDraft({
      total_price: u.total_price ?? '',
      area: u.area ?? '',
      no_of_rooms: u.no_of_rooms ?? '',
      finishing_type: u.finishing_type ?? '',
      delivery_date: u.delivery_date ?? '',
      is_available: u.is_available,
    });
  }

  async function save(id) {
    setBusy(true);
    try {
      const { data } = await apiClient.patch(`/inventory/units/${id}`, draft);
      setUnits((list) => list.map((u) => (u.id === id ? { ...u, ...data } : u)));
      setEditing(null);
    } catch (e) {
      setError(readError(e, 'Could not save that change.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(u) {
    /* Confirmed because it cannot be undone from here, and named so the
       confirmation says what actually disappears. */
    if (!window.confirm(
      `Remove ${u.unit_code || 'this unit'} from ${u.project_name}? This cannot be undone.`
    )) return;
    setBusy(true);
    try {
      await apiClient.delete(`/inventory/units/${u.id}`);
      setUnits((list) => list.filter((x) => x.id !== u.id));
    } catch (e) {
      setError(readError(e, 'Could not remove that unit.'));
    } finally {
      setBusy(false);
    }
  }

  const byCompound = groupBy(units, (u) => u.project_name || 'Unknown compound');
  const compounds = Object.keys(byCompound).sort();
  const infoFor = (name) => compoundInfo.find((c) => c.project_name === name);

  const ALL = '\u0000all';
  const current = (selected === ALL || (selected && byCompound[selected]))
    ? selected
    : ALL;
  const showingAll = current === ALL;
  const rows = showingAll ? units : (current ? byCompound[current] : []);
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? rows.filter((u) => [u.unit_code, u.unit_type, u.finishing_type, u.delivery_date,
                          showingAll ? u.project_name : null]
        .some((f) => String(f ?? '').toLowerCase().includes(needle)))
    : rows;

  /* Past a handful of compounds, finding the right one becomes the work. */
  const cNeedle = compoundQuery.trim().toLowerCase();
  const listed = cNeedle
    ? compounds.filter((n) => n.toLowerCase().includes(cNeedle))
    : compounds;

  function openUnits(name) {
    setSelected(name);
    setQuery('');
    setTab('units');
  }

  return (
    <div className="inv-page">
      <div className="inv-stats">
        <Stat label="Units" value={units.length} />
        <Stat label="Compounds" value={compounds.length} />
        <Stat label="Available" value={units.filter((u) => u.is_available).length} />
      </div>

      {error && <div className="inv-error" role="alert">{error}</div>}

      {loading ? (
        <div className="inv-card"><div className="inv-empty">Loading…</div></div>
      ) : units.length === 0 ? (
        <div className="inv-card">
          <h2 className="inv-title">No inventory yet</h2>
          <p className="inv-desc">
            Upload a sheet of the units you are selling. One file can cover
            several compounds — we work out which is which and ask when a name
            is close to something we already have.
          </p>
        </div>
      ) : (
        <>
          {/* Two ways of looking at the same book: the places, and the stock.
              A broker checking what they cover wants the first; one correcting
              a price wants the second. */}
          <div className="inv-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === 'compounds'}
              className={`inv-tab ${tab === 'compounds' ? 'is-active' : ''}`}
              onClick={() => setTab('compounds')}
            >
              Compounds <span className="inv-tab-count">{compounds.length}</span>
            </button>
            <button
              role="tab"
              aria-selected={tab === 'units'}
              className={`inv-tab ${tab === 'units' ? 'is-active' : ''}`}
              onClick={() => setTab('units')}
            >
              Units <span className="inv-tab-count">{units.length}</span>
            </button>
          </div>

          {tab === 'compounds' ? (
            <>
              {compounds.length > 6 && (
                <input
                  className="inv-search inv-search-compound"
                  value={compoundQuery}
                  onChange={(e) => setCompoundQuery(e.target.value)}
                  placeholder={`Find one of your ${compounds.length} compounds`}
                  aria-label="Filter compounds"
                />
              )}

              <div className="inv-grid">
                {listed.map((name) => (
                  <CompoundCard
                    key={name}
                    name={name}
                    info={infoFor(name)}
                    units={byCompound[name]}
                    open={openSummary === name}
                    onToggle={() => setOpenSummary(openSummary === name ? null : name)}
                    onOpenUnits={() => openUnits(name)}
                  />
                ))}
              </div>

              {listed.length === 0 && (
                <p className="inv-brochure-none">
                  No compound of yours matches “{compoundQuery}”.
                  <button className="inv-link" onClick={() => setCompoundQuery('')}>Clear</button>
                </p>
              )}
            </>
          ) : (
            <div className="inv-card">
              <div className="inv-filters">
                <label className="inv-field">
                  <span className="inv-field-label">Compound</span>
                  <select
                    className="inv-select"
                    value={showingAll ? ALL : current}
                    onChange={(e) => { setSelected(e.target.value); setQuery(''); }}
                  >
                    <option value={ALL}>All compounds ({units.length})</option>
                    {compounds.map((name) => (
                      <option key={name} value={name}>
                        {name} ({byCompound[name].length})
                      </option>
                    ))}
                  </select>
                </label>

                <label className="inv-field inv-field-grow">
                  <span className="inv-field-label">Find a unit</span>
                  <input
                    className="inv-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={showingAll
                      ? 'Code, type, delivery, compound'
                      : 'Code, type, delivery'}
                  />
                </label>
              </div>

              <p className="inv-desc inv-count">
                {shown.length === rows.length
                  ? `${rows.length} ${rows.length === 1 ? 'unit' : 'units'}`
                  : `${shown.length} of ${rows.length} units`}
                {!showingAll && ` in ${current}`}
              </p>

              {shown.length === 0 ? (
                <div className="inv-empty">
                  No unit here matches “{query}”.
                  <button className="inv-link" onClick={() => setQuery('')}>Clear</button>
                </div>
              ) : (
                <div className="inv-scroll">
                  <table className="inv-table">
                    <thead>
                      <tr>
                        <th>Unit</th>
                        {showingAll && <th>Compound</th>}
                        <th>Type</th><th>Rooms</th><th>Area</th>
                        <th>Price</th><th>Delivery</th><th>Status</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((u) => (
                        editing === u.id ? (
                          <tr key={u.id} className="inv-editing">
                            <td className="inv-code">{u.unit_code || '—'}</td>
                            {showingAll && <td className="inv-compound">{u.project_name || '—'}</td>}
                            <td>{u.unit_type || '—'}</td>
                            <td><Field v={draft.no_of_rooms} set={(x) => setDraft({ ...draft, no_of_rooms: x })} w="52" /></td>
                            <td><Field v={draft.area} set={(x) => setDraft({ ...draft, area: x })} w="64" /></td>
                            <td><Field v={draft.total_price} set={(x) => setDraft({ ...draft, total_price: x })} w="96" /></td>
                            <td><Field v={draft.delivery_date} set={(x) => setDraft({ ...draft, delivery_date: x })} w="88" /></td>
                            <td>
                              <label className="inv-check">
                                <input
                                  type="checkbox"
                                  checked={!!draft.is_available}
                                  onChange={(e) => setDraft({ ...draft, is_available: e.target.checked })}
                                />
                                Available
                              </label>
                            </td>
                            <td className="inv-actions">
                              <button className="inv-btn" onClick={() => save(u.id)} disabled={busy}>
                                {busy ? 'Saving…' : 'Save'}
                              </button>
                              <button className="inv-link" onClick={() => setEditing(null)}>Cancel</button>
                            </td>
                          </tr>
                        ) : (
                          <tr key={u.id}>
                            <td className="inv-code">{u.unit_code || '—'}</td>
                            {showingAll && <td className="inv-compound">{u.project_name || '—'}</td>}
                            <td>{u.unit_type || '—'}</td>
                            <td className="inv-num">{u.no_of_rooms ?? '—'}</td>
                            <td className="inv-num">{u.area ? `${Math.round(u.area)} m²` : '—'}</td>
                            <td className="inv-num">{money(u.total_price)}</td>
                            <td>{u.delivery_date || '—'}</td>
                            <td>
                              <span className={`inv-pill ${u.is_available ? 'on' : 'off'}`}>
                                {u.is_available ? 'Available' : 'Withdrawn'}
                              </span>
                            </td>
                            <td className="inv-actions">
                              <button className="inv-link" onClick={() => startEdit(u)}>Edit</button>
                              <button className="inv-link inv-danger" onClick={() => remove(u)}>Remove</button>
                            </td>
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* One compound: the place, not the stock.
 *
 * The image is the point of this view — a broker recognises a compound by
 * sight long before they read its name, and the console has never shown them
 * one. Compounds with no image get a plain plate rather than a broken frame,
 * because a missing photograph is not an error. */
function CompoundCard({ name, info, units, open, onToggle, onOpenUnits }) {
  const live = units.filter((u) => u.is_available).length;
  const prices = units.map((u) => u.total_price).filter((p) => p != null);
  const images = info?.images || [];
  const summary = info?.summary;
  const facilities = info?.facilities;
  const area = info?.average_area;
  const hasText = Boolean(summary || facilities || area);

  return (
    <div className="inv-compound-card">
      {images[0] ? (
        <img className="inv-photo" src={images[0]} alt="" loading="lazy" />
      ) : (
        <div className="inv-photo inv-photo-none" aria-hidden="true">
          {name.slice(0, 1)}
        </div>
      )}

      <div className="inv-compound-body">
        <h3 className="inv-compound-name">{name}</h3>

        {info?.location_description && (
          <p className="inv-compound-where">{info.location_description}</p>
        )}

        <dl className="inv-facts">
          <div><dt>Units</dt><dd>{units.length}</dd></div>
          <div>
            <dt>Available</dt>
            <dd className={live < units.length ? 'inv-fact-warn' : ''}>{live}</dd>
          </div>
          <div>
            <dt>Price</dt>
            <dd>{prices.length
              ? `${money(Math.min(...prices))} – ${money(Math.max(...prices))}`
              : '—'}</dd>
          </div>
        </dl>

        {images.length > 1 && (
          <div className="inv-thumbs">
            {images.slice(1).map((src) => (
              <img key={src} className="inv-thumb" src={src} alt="" loading="lazy" />
            ))}
          </div>
        )}

        <div className="inv-compound-actions">
          <button className="inv-btn" onClick={onOpenUnits}>
            See {units.length} {units.length === 1 ? 'unit' : 'units'}
          </button>
          {hasText ? (
            <button className="inv-link" onClick={onToggle}>
              {open ? 'Hide summary' : 'What buyers are told'}
            </button>
          ) : (
            <span className="inv-compound-nobrochure">No brochure yet</span>
          )}
        </div>

        {open && hasText && (
          <div className="inv-brochure">
            {summary && <p className="inv-brochure-text">{summary}</p>}
            {facilities && (
              <p className="inv-brochure-text">
                <span className="inv-brochure-label">Facilities</span>
                {facilities}
              </p>
            )}
            {area && (
              <p className="inv-brochure-text">
                <span className="inv-brochure-label">Sizes</span>
                {area}
              </p>
            )}
            <p className="inv-brochure-note">
              This is what the advisor quotes to buyers, taken from the
              compound's brochure. Tell us if anything here is wrong.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ v, set, w }) {
  return (
    <input
      className="inv-input"
      style={{ width: `${w}px` }}
      value={v ?? ''}
      onChange={(e) => set(e.target.value)}
    />
  );
}

function Stat({ label, value }) {
  return (
    <div className="inv-stat">
      <span className="inv-stat-value">{value}</span>
      <span className="inv-stat-label">{label}</span>
    </div>
  );
}

/* Prices here run to eight figures; "12,400,000" is harder to read at a glance
   than "12.4M", and brokers say it the second way. */
function money(v) {
  if (v == null) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${Math.round(v / 1000)}K`;
  return String(Math.round(v));
}

function groupBy(list, key) {
  return list.reduce((acc, item) => {
    const k = key(item);
    (acc[k] = acc[k] || []).push(item);
    return acc;
  }, {});
}

function readError(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return fallback;
}
